type BundleLike = { _id: string; items: { _serviceId: string }[] };

export function coveredServiceIds(
  primaryId: string | null | undefined,
  addonIds: Iterable<string>,
  selectedBundleItemIds: Iterable<string> = []
): Set<string> {
  const covered = new Set<string>([...addonIds, ...selectedBundleItemIds]);
  if (primaryId) covered.add(primaryId);
  return covered;
}

export function bundleProgress(itemIds: string[], covered: Set<string>) {
  const selectedCount = itemIds.filter((id) => covered.has(id)).length;
  return {
    selectedCount,
    totalServices: itemIds.length,
    remaining: itemIds.length - selectedCount,
  };
}

const itemIds = (bundle: BundleLike) => bundle.items.map((i) => i._serviceId);

/** Largest bundle this service would complete. */
export function completingBundle<T extends BundleLike>(
  serviceId: string,
  bundles: T[],
  covered: Set<string>,
  exceptBundleId?: string | null
): T | undefined {
  return bundles
    .filter((bundle) => {
      if (bundle._id === exceptBundleId) return false;
      const ids = itemIds(bundle);
      if (!ids.includes(serviceId)) return false;
      return ids.every((id) => id === serviceId || covered.has(id));
    })
    .sort((a, b) => b.items.length - a.items.length)[0];
}

/** Largest fully-covered bundle; won't downgrade from `currentId`. */
export function bestCompleteBundle<T extends BundleLike>(
  bundles: T[],
  covered: Set<string>,
  currentId?: string | null
): T | undefined {
  const complete = bundles.filter((b) => {
    const ids = itemIds(b);
    return ids.length > 0 && ids.every((id) => covered.has(id));
  });
  if (complete.length === 0) return undefined;
  const best = [...complete].sort((a, b) => b.items.length - a.items.length)[0];
  if (currentId) {
    const current = bundles.find((b) => b._id === currentId);
    if (current && current.items.length >= best.items.length) return current;
  }
  return best;
}
